<template>
  <div class="courses-shell pb-12">
    <!-- Premium Glass Header -->
    <v-app-bar flat fixed class="courses-navbar px-4" dark height="70">
      <v-btn icon color="white" class="mr-2" @click="$router.push('/kurse')">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <div class="d-flex align-center">
        <span class="courses-logo-bar mr-3"></span>
        <div>
          <span class="text-h6 font-weight-black white--text tracking-widest uppercase-title">WELL COME FIT</span>
          <span class="caption yellow--text text--darken-2 d-block font-weight-black tracking-widest mt-n1 uppercase-subtitle">MITARBEITERKURSE</span>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn icon large @click="fetchData" :loading="loading" class="refresh-btn">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-app-bar>

    <v-container class="courses-container mt-16 pt-8">
      <!-- Hero Banner -->
      <div class="hero-banner pa-6 pa-md-10 rounded-2xl mb-8 relative-box overflow-hidden">
        <div class="banner-overlay"></div>
        <v-row class="relative-content align-center">
          <v-col cols="12" md="8">
            <span class="banner-tag px-3 py-1 rounded-full text-uppercase font-weight-black mb-3 d-inline-block">
              WELL COME FIT MITARBEITERKURSE
            </span>
            <h1 class="font-weight-black white--text italic-title responsive-hero-title mb-3">Mitarbeiter-Weiterbildungen</h1>
            <p class="grey--text text--lighten-2 body-1 max-width-text leading-relaxed mb-0">
              Erweitere deine Fähigkeiten und vertiefe dein Fachwissen. Melde dich unten für unsere aktuellen Lektionen, Workshops und
              Inhouse-Schulungen an.
            </p>
          </v-col>
          <v-col cols="12" md="4" class="text-center d-none d-md-block">
            <v-icon size="140" color="rgba(251, 192, 45, 0.15)" class="glowing-icon">mdi-school-outline</v-icon>
          </v-col>
        </v-row>
      </div>

      <!-- Filters Toolbar -->
      <v-card dark class="filters-card pa-4 rounded-xl mb-6">
        <v-row align="center" dense>
          <v-col cols="12" sm="5" md="4">
            <v-select
              dark
              v-model="studioFilter"
              :items="studioItems"
              label="Standort (Studio)"
              outlined
              dense
              hide-details
              prepend-inner-icon="mdi-map-marker-outline"
              class="courses-filter-select rounded-lg"></v-select>
          </v-col>
          <v-col cols="12" sm="7" md="8" class="text-sm-right mt-3 mt-sm-0">
            <v-chip-group v-model="statusFilter" mandatory active-class="active-chip">
              <v-chip filter value="all" small outlined>Alle Kurse</v-chip>
              <v-chip filter value="registered" small color="green" outlined>Meine Buchungen</v-chip>
              <v-chip filter value="available" small color="yellow darken-2" outlined>Freie Plätze</v-chip>
              <v-chip filter value="waitlisted" small color="orange" outlined>Warteliste</v-chip>
            </v-chip-group>
          </v-col>
        </v-row>
      </v-card>

      <!-- Calendar Strip Selector (Extremely Mobile Friendly) -->
      <div v-if="!loading && availableDays.length > 0" class="calendar-strip-wrapper mb-6">
        <div class="calendar-strip d-flex align-center gap-3">
          <!-- "All days" card option -->
          <div
            class="calendar-day-card d-flex flex-column align-center justify-center cursor-pointer transition-all"
            :class="{ 'active-day-card': selectedDateKey === 'all' }"
            @click="selectedDateKey = 'all'">
            <v-icon size="20" :color="selectedDateKey === 'all' ? '#000000' : '#fbc02d'">mdi-calendar-multiple</v-icon>
            <span class="day-weekday text-uppercase font-weight-black mt-1" :class="selectedDateKey === 'all' ? 'black--text' : 'grey--text'"
              >Alle</span
            >
          </div>

          <!-- Individual day cards -->
          <div
            v-for="day in availableDays"
            :key="day.dateString"
            class="calendar-day-card d-flex flex-column align-center justify-center cursor-pointer transition-all"
            :class="{ 'active-day-card': selectedDateKey === day.dateString }"
            @click="selectedDateKey = day.dateString">
            <span class="day-weekday text-uppercase font-weight-bold">{{ day.dayName }}</span>
            <span class="day-num font-weight-black">{{ day.dayNum }}</span>
            <span class="day-month text-uppercase">{{ day.monthName }}</span>

            <!-- Indicators: Green for registered, Orange for waitlisted -->
            <div class="day-indicator-dots d-flex gap-1 mt-1">
              <span v-if="day.hasBooking" class="indicator-dot dot-green"></span>
              <span v-if="day.hasWaitlist" class="indicator-dot dot-orange"></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading / Empty States -->
      <div v-if="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="#fbc02d" size="64" class="mb-4"></v-progress-circular>
        <div class="grey--text text--lighten-1 subtitle-1">Lade verfügbare Kurse...</div>
      </div>

      <div v-else-if="groupedCourses.length === 0" class="text-center py-12 empty-state rounded-2xl">
        <v-icon size="64" color="grey darken-2" class="mb-4">mdi-calendar-blank</v-icon>
        <h3 class="text-h6 font-weight-bold white--text mb-1">Keine Kurse gefunden</h3>
        <p class="grey--text caption max-width-text mx-auto px-4">
          Für die gewählten Filter sind zurzeit keine Mitarbeiter-Lektionen geplant. Ändere die Filter oder lade die Seite neu.
        </p>
      </div>

      <!-- Grouped Courses List -->
      <div v-else>
        <div v-for="group in groupedCourses" :key="group.date" class="mb-8 width-100">
          <!-- Elegant Date Header -->
          <div class="d-flex align-center mb-4 pb-2 border-bottom-header">
            <span class="date-header-dot mr-2"></span>
            <h3 class="text-h6 font-weight-black white--text tracking-wide">{{ group.date }}</h3>
            <v-spacer></v-spacer>
            <span class="caption grey--text font-weight-bold">{{ group.courses.length }} Lektionen</span>
          </div>

          <!-- Courses Grid for this Date -->
          <v-row class="courses-grid">
            <v-col v-for="course in group.courses" :key="course._id" cols="12" sm="6" lg="4" class="d-flex">
              <v-card
                dark
                class="course-card rounded-xl d-flex flex-column width-100 flex-grow-1"
                :class="{ 'border-registered': isUserRegistered(course) }"
                @click="openCourseDetails(course)">
                <!-- Card Image/Header Accent -->
                <div class="card-header-accent pa-4 d-flex justify-space-between align-start relative-box">
                  <v-chip small :color="getSpotsColor(course)" class="font-weight-bold black--text">
                    {{ getSpotsText(course) }}
                  </v-chip>

                  <v-chip v-if="isUserRegistered(course)" small color="green" class="font-weight-bold black--text">
                    <v-icon x-small left>mdi-check</v-icon> Gebucht
                  </v-chip>
                  <v-chip v-else-if="isUserWaitlisted(course)" small color="orange" class="font-weight-bold black--text">
                    <v-icon x-small left>mdi-clock-outline</v-icon> Warteliste (Pos. {{ getUserWaitlistPos(course) }})
                  </v-chip>
                </div>

                <!-- Card Info -->
                <v-card-text class="pa-5 pt-2 flex-grow-1 d-flex flex-column">
                  <div class="d-flex align-center mb-2">
                    <v-icon x-small color="grey lighten-1" class="mr-1">mdi-map-marker</v-icon>
                    <span class="caption grey--text font-weight-bold">{{ course.studio ? course.studio.name : "WELL COME FIT" }}</span>
                  </div>

                  <h3 class="white--text font-weight-bold text-h6 mb-1 line-clamp-2" style="line-height: 1.25">
                    {{ course.format ? course.format.title : "Mitarbeiterkurs" }}
                  </h3>

                  <!-- Optional timeslot specific details -->
                  <div v-if="course.details" class="caption yellow--text text--darken-2 mb-2 font-italic font-weight-medium">
                    {{ course.details }}
                  </div>

                  <div class="divider-thin mb-3"></div>

                  <!-- Time Info -->
                  <div class="d-flex align-center mb-3">
                    <v-icon small color="#fbc02d" class="mr-2">mdi-clock-outline</v-icon>
                    <span class="body-2 white--text font-weight-medium">
                      {{ formatLocalTime(course.dateTime) }} Uhr <span class="grey--text">({{ course.duration }} Min)</span>
                    </span>
                  </div>

                  <!-- Abmeldefrist Alert info -->
                  <div class="mt-auto pt-3">
                    <div v-if="isDeadlinePassed(course)" class="caption red--text text--lighten-2 d-flex align-center">
                      <v-icon x-small color="red" class="mr-1">mdi-lock-outline</v-icon>
                      Abmeldefrist abgelaufen
                    </div>
                    <div v-else class="caption grey--text d-flex align-center">
                      <v-icon x-small color="grey" class="mr-1">mdi-clock-alert-outline</v-icon>
                      Abmeldung bis: {{ formatShortDateTime(course.cancellationDeadline) }}
                    </div>
                  </div>
                </v-card-text>

                <!-- Card Actions -->
                <v-card-actions class="pa-4 pt-0">
                  <v-btn
                    v-if="isUserRegistered(course) || isUserWaitlisted(course)"
                    block
                    outlined
                    color="red lighten-1"
                    class="rounded-lg font-weight-bold uppercase-btn"
                    :disabled="isDeadlinePassed(course)"
                    @click.stop="confirmUnregistration(course)">
                    {{ isDeadlinePassed(course) ? "Gesperrt" : "Abmelden" }}
                  </v-btn>
                  <v-btn
                    v-else-if="hasAvailableSpots(course)"
                    block
                    color="#fbc02d"
                    class="black--text rounded-lg font-weight-bold uppercase-btn register-glowing-btn"
                    :loading="registeringId === course._id"
                    @click.stop="registerForCourse(course)">
                    Anmelden
                  </v-btn>
                  <v-btn
                    v-else-if="course.waitlistEnabled"
                    block
                    color="orange"
                    class="black--text rounded-lg font-weight-bold uppercase-btn"
                    :loading="registeringId === course._id"
                    @click.stop="registerForCourse(course)">
                    Auf Warteliste
                  </v-btn>
                  <v-btn v-else block disabled class="rounded-lg font-weight-bold uppercase-btn"> Ausgebucht </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-container>

    <!-- Detailed Dialog Modal -->
    <v-dialog v-model="detailDialog" max-width="600px" class="rounded-2xl" dark>
      <v-card v-if="selectedCourse" class="detail-card pa-6 rounded-2xl border-highlight" style="background-color: #1c1c1e">
        <div class="d-flex align-start justify-space-between mb-4">
          <div>
            <span class="caption yellow--text text--darken-2 font-weight-black text-uppercase tracking-widest"> Kursdetails & Anmeldung </span>
            <h2 class="text-h5 font-weight-black white--text italic-title mt-1" style="line-height: 1.2">
              {{ selectedCourse.format ? selectedCourse.format.title : "Mitarbeiterkurs" }}
            </h2>
            <div v-if="selectedCourse.details" class="caption yellow--text text--darken-2 font-italic font-weight-bold mt-1">
              * {{ selectedCourse.details }}
            </div>
          </div>
          <v-btn icon color="grey" @click="detailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="divider-thin mb-4"></div>

        <!-- Description -->
        <div class="mb-6">
          <h4 class="caption grey--text text-uppercase font-weight-black tracking-wider mb-2">Beschreibung</h4>
          <p class="body-2 grey--text text--lighten-2 leading-relaxed" style="white-space: pre-line">
            {{ selectedCourse.format ? selectedCourse.format.description : "Keine Beschreibung hinterlegt." }}
          </p>
        </div>

        <!-- Meta Grid -->
        <v-row class="mb-6" dense>
          <v-col cols="6" class="pa-2">
            <div class="meta-box pa-3 rounded-xl text-center">
              <v-icon color="#fbc02d" class="mb-1">mdi-map-marker-outline</v-icon>
              <div class="caption grey--text">Standort</div>
              <div class="body-2 font-weight-bold white--text mt-1">
                {{ selectedCourse.studio ? selectedCourse.studio.name : "WELL COME FIT" }}
              </div>
            </div>
          </v-col>

          <v-col cols="6" class="pa-2">
            <div class="meta-box pa-3 rounded-xl text-center">
              <v-icon color="#fbc02d" class="mb-1">mdi-account-group-outline</v-icon>
              <div class="caption grey--text">Belegung</div>
              <div class="body-2 font-weight-bold white--text mt-1">{{ selectedCourse.registeredCount }} / {{ selectedCourse.capacity }} Plätze</div>
            </div>
          </v-col>

          <v-col cols="6" class="pa-2">
            <div class="meta-box pa-3 rounded-xl text-center">
              <v-icon color="#fbc02d" class="mb-1">mdi-calendar-clock</v-icon>
              <div class="caption grey--text">Termin</div>
              <div class="body-2 font-weight-bold white--text mt-1">
                {{ formatLocalDate(selectedCourse.dateTime) }}
              </div>
              <div class="caption grey--text mt-0-5">{{ formatLocalTime(selectedCourse.dateTime) }} Uhr ({{ selectedCourse.duration }}m)</div>
            </div>
          </v-col>

          <v-col cols="6" class="pa-2">
            <div class="meta-box pa-3 rounded-xl text-center">
              <v-icon color="#fbc02d" class="mb-1">mdi-timer-sand</v-icon>
              <div class="caption grey--text">Warteliste</div>
              <div class="body-2 font-weight-bold white--text mt-1">
                {{ selectedCourse.waitlistEnabled ? `${selectedCourse.waitlistCount} Personen` : "Deaktiviert" }}
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Cancellation Box Warning -->
        <div class="notice-box pa-4 rounded-xl mb-6" :class="isDeadlinePassed(selectedCourse) ? 'border-red-notice' : 'border-gold-notice'">
          <div class="d-flex align-start">
            <v-icon :color="isDeadlinePassed(selectedCourse) ? 'red' : '#fbc02d'" class="mr-3 mt-1">
              {{ isDeadlinePassed(selectedCourse) ? "mdi-alert-circle-outline" : "mdi-alert-decagram-outline" }}
            </v-icon>
            <div>
              <h4 class="white--text font-weight-black subtitle-2 text-uppercase tracking-wider mb-1">Abmeldefrist (Cancellation Deadline)</h4>
              <p class="caption grey--text text--lighten-2 leading-relaxed mb-0" v-if="isDeadlinePassed(selectedCourse)">
                Die Abmeldefrist für diesen Kurs ist abgelaufen. Eine Abmeldung über diese App ist nicht mehr möglich. Falls du aus dringenden Gründen
                absagen musst, wende dich bitte direkt an deinen Vorgesetzten/Manager.
              </p>
              <p class="caption grey--text text--lighten-2 leading-relaxed mb-0" v-else>
                Du kannst dich bis zum <strong>{{ formatShortDateTime(selectedCourse.cancellationDeadline) }} Uhr</strong> abmelden. Danach wird die
                Buchung gesperrt und eine Stornierung ist nur noch für Manager möglich.
              </p>
            </div>
          </div>
        </div>

        <!-- Registration Actions in Dialog -->
        <div class="d-flex gap-3 justify-end">
          <v-btn text color="grey lighten-1" @click="detailDialog = false" class="rounded-lg">Schliessen</v-btn>

          <v-btn
            v-if="isUserRegistered(selectedCourse) || isUserWaitlisted(selectedCourse)"
            color="red lighten-1"
            class="black--text rounded-lg font-weight-bold px-6"
            :disabled="isDeadlinePassed(selectedCourse)"
            @click="confirmUnregistration(selectedCourse)">
            {{ isDeadlinePassed(selectedCourse) ? "Stornierung gesperrt" : "Vom Kurs abmelden" }}
          </v-btn>

          <v-btn
            v-else-if="hasAvailableSpots(selectedCourse)"
            color="#fbc02d"
            class="black--text rounded-lg font-weight-bold px-6 register-glowing-btn"
            :loading="registeringId === selectedCourse._id"
            @click="registerForCourse(selectedCourse)">
            Anmelden
          </v-btn>

          <v-btn
            v-else-if="selectedCourse.waitlistEnabled"
            color="orange"
            class="black--text rounded-lg font-weight-bold px-6"
            :loading="registeringId === selectedCourse._id"
            @click="registerForCourse(selectedCourse)">
            Auf Warteliste eintragen
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Confirm Unregistration Dialog -->
    <v-dialog v-model="confirmUnregDialog.show" max-width="400px" dark>
      <v-card class="pa-4 rounded-xl" style="background-color: #1c1c1e">
        <h3 class="text-h6 font-weight-bold white--text mb-2">Abmeldung bestätigen?</h3>
        <p class="grey--text caption leading-relaxed mb-4">
          Möchtest du dich wirklich vom Kurs <strong>{{ confirmUnregDialog.courseTitle }}</strong> abmelden? Falls du auf der Hauptliste stehst, rückt
          die nächste Person von der Warteliste automatisch nach.
        </p>
        <div class="d-flex justify-end gap-3">
          <v-btn text color="grey" @click="confirmUnregDialog.show = false">Abbrechen</v-btn>
          <v-btn
            color="red lighten-1"
            class="black--text font-weight-bold rounded-lg"
            :loading="confirmUnregDialog.loading"
            @click="executeUnregistration">
            Ja, abmelden
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"

export default {
  name: "EmployeeCoursesRegistration",
  data() {
    return {
      loading: true,
      courses: [],
      studioFilter: "all",
      statusFilter: "all",
      selectedDateKey: "all",
      registeringId: null,

      detailDialog: false,
      selectedCourse: null,

      confirmUnregDialog: {
        show: false,
        courseId: null,
        courseTitle: null,
        loading: false,
      },
    }
  },
  watch: {
    // Reset selected date if filters change and make the previously selected day unavailable
    availableDays(newDays) {
      if (this.selectedDateKey !== "all" && !newDays.some((d) => d.dateString === this.selectedDateKey)) {
        this.selectedDateKey = "all"
      }
    },
  },
  computed: {
    // Dynamic list of studios from loaded courses
    studioItems() {
      const items = [{ text: "Alle Standorte", value: "all" }]
      const map = new Map()

      this.courses.forEach((c) => {
        if (c.studio && c.studio._id) {
          map.set(c.studio._id.toString(), c.studio.name)
        }
      })

      map.forEach((name, id) => {
        items.push({ text: name, value: id })
      })

      return items
    },

    // Apply client-side filters (Studio & Status)
    filteredCourses() {
      let list = this.courses

      // 1. Studio filter
      if (this.studioFilter !== "all") {
        list = list.filter((c) => c.studio && c.studio._id.toString() === this.studioFilter)
      }

      // 2. Status filter
      if (this.statusFilter === "registered") {
        list = list.filter((c) => this.isUserRegistered(c) || this.isUserWaitlisted(c))
      } else if (this.statusFilter === "available") {
        list = list.filter((c) => !this.isUserRegistered(c) && !this.isUserWaitlisted(c) && this.hasAvailableSpots(c))
      } else if (this.statusFilter === "waitlisted") {
        list = list.filter((c) => this.isUserWaitlisted(c))
      }

      return list
    },

    // Extracts all unique calendar days that have courses based on active filters
    availableDays() {
      const daysMap = new Map()

      this.filteredCourses.forEach((course) => {
        if (!course.dateTime) return
        const d = new Date(course.dateTime)
        const dateString = d.toISOString().split("T")[0] // YYYY-MM-DD

        if (!daysMap.has(dateString)) {
          daysMap.set(dateString, {
            dateString,
            dateObj: d,
            dayName: d.toLocaleDateString("de-CH", { weekday: "short" }),
            dayNum: d.getDate(),
            monthName: d.toLocaleDateString("de-CH", { month: "short" }),
            hasBooking: false,
            hasWaitlist: false,
            coursesCount: 0,
          })
        }

        const dayInfo = daysMap.get(dateString)
        dayInfo.coursesCount++
        if (this.isUserRegistered(course)) dayInfo.hasBooking = true
        if (this.isUserWaitlisted(course)) dayInfo.hasWaitlist = true
      })

      return Array.from(daysMap.values()).sort((a, b) => a.dateObj - b.dateObj)
    },

    // Groups the filtered courses by local date (optionally filtered by calendar strip)
    groupedCourses() {
      const groups = {}
      this.filteredCourses.forEach((course) => {
        if (!course.dateTime) return
        const dateObj = new Date(course.dateTime)
        const dateKeyString = dateObj.toISOString().split("T")[0] // YYYY-MM-DD

        // Filter by calendar strip day if a specific day is selected
        if (this.selectedDateKey !== "all" && dateKeyString !== this.selectedDateKey) {
          return
        }

        const dateKey = dateObj.toLocaleDateString("de-CH", {
          weekday: "long",
          day: "2-digit",
          month: "long",
          year: "numeric",
        })

        if (!groups[dateKey]) {
          groups[dateKey] = []
        }
        groups[dateKey].push(course)
      })

      return Object.keys(groups).map((date) => ({
        date,
        courses: groups[date],
      }))
    },
  },
  async created() {
    await this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const data = await api.employeeCourses.fetchCourses()
        this.courses = data || []

        // Refresh selected course details if dialog is open
        if (this.selectedCourse) {
          const updated = this.courses.find((c) => c._id === this.selectedCourse._id)
          if (updated) this.selectedCourse = updated
        }
      } catch (error) {
        console.error(error)
        this.$toast.error("Kurse konnten nicht geladen werden.")
      } finally {
        this.loading = false
      }
    },

    formatLocalDate(dateStr) {
      if (!dateStr) return ""
      return new Date(dateStr).toLocaleDateString("de-CH", {
        weekday: "short",
        day: "2-digit",
        month: "long",
      })
    },

    formatLocalTime(dateStr) {
      if (!dateStr) return ""
      return new Date(dateStr).toLocaleTimeString("de-CH", {
        hour: "2-digit",
        minute: "2-digit",
      })
    },

    formatShortDateTime(dateStr) {
      if (!dateStr) return ""
      return (
        new Date(dateStr).toLocaleDateString("de-CH", {
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }) + " Uhr"
      )
    },

    isDeadlinePassed(course) {
      if (!course || !course.cancellationDeadline) return true
      return new Date() >= new Date(course.cancellationDeadline)
    },

    isUserRegistered(course) {
      return course.userRegistration && course.userRegistration.status === "registered"
    },

    isUserWaitlisted(course) {
      return course.userRegistration && course.userRegistration.status === "waitlisted"
    },

    getUserWaitlistPos(course) {
      return course.userRegistration ? course.userRegistration.waitlistPosition : 0
    },

    hasAvailableSpots(course) {
      return course.registeredCount < course.capacity
    },

    getSpotsColor(course) {
      const free = course.capacity - course.registeredCount
      if (free <= 0) return "grey lighten-1"
      if (free <= 3) return "orange"
      return "#fbc02d"
    },

    getSpotsText(course) {
      const free = course.capacity - course.registeredCount
      if (free <= 0) {
        return course.waitlistEnabled ? "Warteliste aktiv" : "Ausgebucht"
      }
      return `${free} von ${course.capacity} Plätzen frei`
    },

    openCourseDetails(course) {
      this.selectedCourse = course
      this.detailDialog = true
    },

    async registerForCourse(course) {
      this.registeringId = course._id
      try {
        const res = await api.employeeCourses.register(course._id)
        this.$toast.success(res.message || "Erfolgreich angemeldet!")
        await this.fetchData()
      } catch (error) {
        console.error(error)
        this.$toast.error(error.response?.data || "Anmeldung fehlgeschlagen.")
      } finally {
        this.registeringId = null
      }
    },

    confirmUnregistration(course) {
      const title = course.format ? course.format.title : "Mitarbeiterkurs"
      this.confirmUnregDialog = {
        show: true,
        courseId: course._id,
        courseTitle: title,
        loading: false,
      }
    },

    async executeUnregistration() {
      this.confirmUnregDialog.loading = true
      try {
        await api.employeeCourses.unregister(this.confirmUnregDialog.courseId)
        this.$toast.success("Erfolgreich abgemeldet.")
        this.confirmUnregDialog.show = false
        this.detailDialog = false
        await this.fetchData()
      } catch (error) {
        console.error(error)
        this.$toast.error(error.response?.data || "Abmeldung fehlgeschlagen.")
      } finally {
        this.confirmUnregDialog.loading = false
      }
    },
  },
}
</script>

<style scoped>
.courses-shell {
  background-color: #0f0f0f;
  min-height: 100vh;
  color: white;
}

.courses-navbar {
  background-color: rgba(15, 15, 15, 0.75) !important;
  backdrop-filter: blur(15px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.courses-logo-bar {
  display: inline-block;
  width: 4px;
  height: 28px;
  background-color: #fbc02d;
  border-radius: 2px;
}

.uppercase-title {
  font-size: 14px !important;
  letter-spacing: 2px !important;
  font-weight: 950 !important;
}

.uppercase-subtitle {
  font-size: 10px !important;
  letter-spacing: 3px !important;
}

.courses-container {
  max-width: 1200px;
  margin: 0 auto;
}

/* Hero Banner */
.hero-banner {
  background: linear-gradient(135deg, #1c1c1e 0%, #0c0c0e 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.4);
}

.banner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 80% 50%, rgba(251, 192, 45, 0.08) 0%, transparent 60%);
  pointer-events: none;
}

.banner-tag {
  background-color: #fbc02d;
  color: #000000;
  font-size: 10px;
  letter-spacing: 1px;
}

.italic-title {
  font-style: italic;
  font-weight: 950;
  letter-spacing: -0.5px;
}

.responsive-hero-title {
  font-size: 42px;
  line-height: 1.1;
}

@media (max-width: 600px) {
  .responsive-hero-title {
    font-size: 26px !important;
    line-height: 1.2 !important;
  }
}

.max-width-text {
  max-width: 700px;
}

.leading-relaxed {
  line-height: 1.6;
}

.info-badge {
  background-color: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.glowing-icon {
  filter: drop-shadow(0 0 15px rgba(251, 192, 45, 0.2));
  animation: pulse 4s infinite alternate;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1.05);
    opacity: 1;
  }
}

/* Filters */
.filters-card {
  background-color: #1c1c1e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.courses-filter-select :deep(.v-input__control) {
  border-radius: 8px !important;
  background: rgba(255, 255, 255, 0.03) !important;
  background-color: rgba(255, 255, 255, 0.03) !important;
}

.courses-filter-select :deep(.v-input__slot) {
  background: rgba(255, 255, 255, 0.03) !important;
  background-color: rgba(255, 255, 255, 0.03) !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.courses-filter-select :deep(.v-select__selection) {
  color: white !important;
}

.courses-filter-select :deep(.v-icon) {
  color: #fbc02d !important;
}

.active-chip {
  background-color: rgba(251, 192, 45, 0.1) !important;
  color: #fbc02d !important;
  border-color: #fbc02d !important;
}

/* Calendar Day Strip */
.calendar-strip-wrapper {
  overflow-x: auto;
  padding: 8px 4px 12px 4px;
  margin: 0 -4px;
  scrollbar-width: none; /* Firefox */
}

.calendar-strip-wrapper::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
}

.calendar-strip {
  display: inline-flex;
}

.calendar-day-card {
  width: 70px;
  height: 90px;
  flex-shrink: 0;
  background-color: #1c1c1e;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  color: #a0a0a5;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.calendar-day-card:hover {
  border-color: rgba(251, 192, 45, 0.4);
  transform: translateY(-2px);
}

.active-day-card {
  background: linear-gradient(135deg, #fbc02d 0%, #f9a825 100%) !important;
  border-color: #fbc02d !important;
  transform: scale(1.08) translateY(-2px);
  box-shadow: 0 6px 20px rgba(251, 192, 45, 0.35) !important;
  color: #000000 !important;
}

.day-weekday {
  font-size: 10px;
  letter-spacing: 1px;
  font-weight: 800;
}

.day-num {
  font-size: 22px;
  line-height: 1.1;
  margin: 2px 0;
  color: white;
}

.active-day-card .day-num {
  color: #000000 !important;
}

.day-month {
  font-size: 10px;
  letter-spacing: 0.5px;
}

/* Dots underneath the days indicating bookings */
.day-indicator-dots {
  position: absolute;
  bottom: 6px;
}

.indicator-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  display: inline-block;
}

.dot-green {
  background-color: #4caf50;
  box-shadow: 0 0 4px #4caf50;
}

.dot-orange {
  background-color: #ff9800;
  box-shadow: 0 0 4px #ff9800;
}

.active-day-card .dot-green {
  background-color: #1b5e20;
  box-shadow: none;
}

.active-day-card .dot-orange {
  background-color: #e65100;
  box-shadow: none;
}

/* Course Grouping Headers */
.border-bottom-header {
  border-bottom: 2px solid rgba(255, 255, 255, 0.08);
}

.date-header-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  background-color: #fbc02d;
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(251, 192, 45, 0.6);
}

/* Course Cards */
.course-card {
  background-color: #1c1c1e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.course-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(251, 192, 45, 0.1) !important;
  border-color: rgba(251, 192, 45, 0.3) !important;
}

.border-registered {
  border-color: rgba(76, 175, 80, 0.4) !important;
  box-shadow: 0 8px 24px rgba(76, 175, 80, 0.05) !important;
}

.card-header-accent {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.03) 0%, transparent 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.divider-thin {
  height: 1px;
  background-color: rgba(255, 255, 255, 0.06);
}

.uppercase-btn {
  text-transform: uppercase;
  font-size: 12px !important;
  letter-spacing: 1px;
}

.register-glowing-btn {
  box-shadow: 0 0 15px rgba(251, 192, 45, 0.2);
}

.register-glowing-btn:hover {
  box-shadow: 0 0 25px rgba(251, 192, 45, 0.4);
}

/* Detail Card Modal */
.detail-card {
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.5) !important;
}

.meta-box {
  background-color: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  height: 100%;
}

.notice-box {
  background-color: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.border-gold-notice {
  border-left: 4px solid #fbc02d;
}

.border-red-notice {
  border-left: 4px solid #f50057;
}

/* Helper Utilities */
.width-100 {
  width: 100%;
}

.gap-1 {
  gap: 4px;
}

.gap-3 {
  gap: 12px;
}

.gap-4 {
  gap: 16px;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.empty-state {
  background-color: #1c1c1e;
  border: 1px dashed rgba(255, 255, 255, 0.1);
}
</style>
